import React from 'react';
import { FiPhone, FiMail, FiMapPin, FiClock } from 'react-icons/fi';
import { FaFacebook, FaInstagram, FaTiktok } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { businessData } from './seoConfig';
import { IconWrapper } from './IconWrapper';

export const Contact: React.FC = () => {
  const contactItems = [
    {
      icon: FiPhone,
      label: 'Phone',
      value: businessData.phone,
      link: `tel:${businessData.phone}`,
    },
    {
      icon: FiMail,
      label: 'Email',
      value: 'info@gigihairbraiding.com',
      link: 'mailto:info@gigihairbraiding.com',
    },
    {
      icon: FiMapPin,
      label: 'Address',
      value: businessData.address,
      link: `https://maps.google.com/?q=${encodeURIComponent(
        businessData.address
      )}`,
    },
  ];

  const hours = [
    { day: 'Monday', time: businessData.hours.monday },
    { day: 'Tuesday', time: businessData.hours.tuesday },
    { day: 'Wednesday', time: businessData.hours.wednesday },
    { day: 'Thursday', time: businessData.hours.thursday },
    { day: 'Friday', time: businessData.hours.friday },
    { day: 'Saturday', time: businessData.hours.saturday },
    { day: 'Sunday', time: businessData.hours.sunday },
  ];

  return (
    <>
      {/* Hero Section */}
      <motion.section 
        className="pt-32 pb-20 px-6 md:px-20 bg-[#FAF8F4]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm tracking-widest uppercase text-[#6B6970] mb-4">
            Get In Touch
          </p>
          <h1 className="font-serif text-6xl md:text-7xl text-[#0B0B0B] mb-6 leading-tight">
            Contact Us
          </h1>
          <p className="text-xl text-[#6B6970] max-w-2xl mx-auto leading-relaxed">
            Located in the heart of {businessData.city}, {businessData.state}
          </p>
        </div>
      </motion.section>

      {/* Main Contact Section */}
      <motion.section 
        className="py-32 px-6 md:px-20 bg-white"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Contact Cards */}
          <motion.div 
            className="grid md:grid-cols-3 gap-8 mb-16"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {contactItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.a
                  key={index}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white p-8 hover:shadow-lg transition-all group cursor-pointer border-2 border-[#E8DDD8] hover:border-[#C97B63] rounded-2xl"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 }
                  }}
                  whileHover={{ y: -4 }}
                >
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="bg-[#C97B63] p-4 rounded-2xl group-hover:bg-[#0B0B0B] transition-all shadow-sm">
                      <IconWrapper className="w-8 h-8 text-white">
                        <Icon />
                      </IconWrapper>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#0B0B0B] mb-2 text-lg tracking-widest uppercase">
                        {item.label}
                      </h3>
                      <p className="text-[#6B6970] group-hover:text-[#0B0B0B] transition-colors">
                        {item.value}
                      </p>
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </motion.div>

          {/* Hours & Social */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Hours */}
            <div className="bg-white p-10 shadow-sm border-l-4 border-[#C97B63] rounded-2xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-[#C97B63] rounded-2xl">
                  <FiClock className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-serif text-2xl text-[#0B0B0B] tracking-wide">
                  Hours
                </h3>
              </div>
              <div className="space-y-4">
                {hours.map((hour, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center pb-4 border-b border-[#E8DDD8] last:border-0"
                  >
                    <span className="font-medium text-[#0B0B0B] text-sm tracking-widest uppercase">
                      {hour.day}
                    </span>
                    <span
                      className={`font-medium ${
                        hour.time === 'CLOSED'
                          ? 'text-[#C97B63]'
                          : 'text-[#0B0B0B]'
                      }`}
                    >
                      {hour.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social & CTA */}
            <div className="space-y-8">
              {/* Social Links */}
              <div className="bg-white p-10 shadow-sm border-l-4 border-[#C97B63] rounded-2xl">
                <h3 className="font-serif text-2xl text-[#0B0B0B] mb-6">
                  Follow Us
                </h3>
                <div className="flex gap-4">
                  <a
                    href={businessData.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 p-6 bg-[#FAF8F4] hover:bg-blue-500 transition-all group shadow-sm text-center rounded-2xl"
                    aria-label="Facebook"
                  >
                    <FaFacebook className="w-8 h-8 mx-auto text-[#0B0B0B] group-hover:text-white transition-colors" />
                  </a>
                  <a
                    href={businessData.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 p-6 bg-[#FAF8F4] hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 transition-all group shadow-sm text-center rounded-2xl"
                    aria-label="Instagram"
                  >
                    <FaInstagram className="w-8 h-8 mx-auto text-[#0B0B0B] group-hover:text-white transition-colors" />
                  </a>
                  <a
                    href={businessData.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 p-6 bg-[#FAF8F4] hover:bg-black transition-all group shadow-sm text-center rounded-2xl"
                    aria-label="TikTok"
                  >
                    <FaTiktok className="w-8 h-8 mx-auto text-[#0B0B0B] group-hover:text-white transition-colors" />
                  </a>
                </div>
              </div>

              {/* CTA */}
              <a
                href={`tel:${businessData.phone}`}
                className="block bg-[#0B0B0B] text-white p-10 hover:shadow-lg transition-all text-center shadow-sm rounded-2xl"
              >
                <p className="text-sm text-white/80 mb-2 uppercase tracking-widest">
                  Ready to book?
                </p>
                <p className="font-serif text-3xl mb-2">
                  {businessData.phone}
                </p>
                <p className="text-white/90">
                  Call us today!
                </p>
              </a>
            </div>
          </div>

          {/* Map Embed */}
          <div className="overflow-hidden shadow-lg h-96 border-2 border-[#E8DDD8] rounded-2xl">
            <iframe
              title="GiGi Hair Braiding Location"
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.5892547634646!2d${
                businessData.coordinates.longitude
              }!3d${
                businessData.coordinates.latitude
              }!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s${encodeURIComponent(
                businessData.address
              )}!2sGiGi%20Hair%20Braiding!5e0!3m2!1sen!2sus!4v1234567890`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default Contact;
